import { GridColDef } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useNavigate } from "react-router-dom";
import ListPageDX from "../../components/business/listpagedx";
import { useNotificationContext } from "../../context/notificationcontext";
import usePackingMaterialService from "../../shared/services/packingmaterialservice";
import TypeTranslator from "../../shared/typetranslator";
import PackingMaterialBulkForm from "../../components/units_forms/packingmaterials/packingmaterialsbulkform";
import { PrintDocumentWithBarcode } from "../../shared/pdfoperations";

const PackingMaterial = (props: any) => {
	const { t, i18n } = useTranslation();
	const { setAddRecordfn, setAddRecordLabel } = useOutletContext() as any;
	const { setInfo, setError } = useNotificationContext();
	const { getPackingMaterials, archivePackingMaterial } =
		usePackingMaterialService();
	const { unitTypes } = TypeTranslator();

	const [rows, setRows] = useState<any>([]);
	const [dataFromApi, setDataFromApi] = useState<any>([]);
	const [importForm, setImportForm] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();

	const columns: GridColDef[] = [
		{
			field: "name",
			headerName: `${t("Name")}`,
			minWidth: 150,
			flex: 1,
		},
		{
			field: "sku",
			headerName: `${t("Code")}`,
			minWidth: 150,
		},
		{
			field: "size",
			headerName: `${t("Size (L x W x H) Unit")}`,
			headerAlign: "center",
			align: "center",
			minWidth: 150,
			valueGetter(params) {
				const rowValue = params.row;
				return `${rowValue.length} x ${rowValue.width} x ${rowValue.height} ${
					unitTypes[rowValue.unitTypeId].text
				}`;
			},
		},
		{
			field: "numOfUnits",
			headerName: `${t("Quantity")}`,
			headerAlign: "center",
			align: "center",
			width: 130,
		},
	];

	const getData = () => {
		setIsLoading(true);
		getPackingMaterials()
			.then((data) => {
				setDataFromApi(data);
				setRows(data);
			})
			.catch((err) => setError(err))
			.finally(() => setIsLoading(false));
	};

	const toCreate = () => {
		navigate("/packingmaterialdetails", {
			state: {
				packageData: null,
			},
		});
	};
	const onEdit = (data: any) => {
		navigate(`/packingmaterialdetails`, {
			state: {
				packageData: data,
			},
		});
	};

	const onDelete = (id: number) => {
		setIsLoading(true);
		archivePackingMaterial(id)
			.then(() => {
				setInfo(t("Package material archived successfully"));
				getData();
			})
			.catch((err) => setError(err))
			.finally(() => setIsLoading(false));
	};

	const toImport = () => {
		setImportForm(true);
	};

	const setGridFilterCriteria = (value: string) => {
		if (!value.trim()) {
			setRows(dataFromApi);
			return;
		}
		const newRows = dataFromApi.filter(
			(row: any) =>
				row.name.toLowerCase().includes(value.toLowerCase()) ||
				row.code.toLowerCase().includes(value.toLowerCase())
		);
		setRows(newRows);
	};

	const onClose = (refreshPage: boolean = false) => {
		setImportForm(false);

		if (refreshPage) getData();
	};

	useEffect(() => {
		getData();
		setAddRecordfn(() => toCreate);
		setAddRecordLabel(t("Add Packing Material"));

		return () => {
			setAddRecordfn(() => {});
			setAddRecordLabel(t("Add Record"));
		};
	}, [i18n.language]);

	return (
		<>
			{importForm && (
				<PackingMaterialBulkForm open={importForm} handleClose={onClose} />
			)}
			<ListPageDX
				listTitle={t("Packing Materials")}
				name={t("Packing Materials")}
				rows={rows}
				columns={columns}
				getRowId={(row: any) => row.packingMaterialId}
				isLoading={isLoading}
				onEdit={onEdit}
				onDelete={onDelete}
				editLabel={t("Preview")}
				setGridFilterCriteria={setGridFilterCriteria}
				exportToPDF={false}
				exportToCSV={false}
				showDatePicker={false}
				showSelect={false}
				showImportButton
				onImport={toImport}
				importTemplateLink={"../assets/templates/PackingMaterialTemplate.csv"}
				onPrint={(data: any) => PrintDocumentWithBarcode(`${data.sku}`)}
			/>
		</>
	);
};

export default PackingMaterial;
